import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, LockKey, Network, Stack, Globe, Cpu, ShieldCheck, GitBranch, Buildings, HandHeart } from "@phosphor-icons/react";
import { PageHeader } from "../components/PageHeader";
import { FadeUp, Overline } from "../components/Reveal";
import {
  ASSESSMENTS,
  CONSULTING,
  FUNCTION_GROUPS,
  GOVERNMENT_MODE,
  INDUSTRY_DOMAINS,
  MARKETPLACE,
  NONPROFIT_MODE,
  RISKS,
  ROLES,
} from "../data/universal";

const slug = (value) => value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const Select = ({ label, value, onChange, options, testId, disabled = false }) => (
  <label className="block">
    <span className="text-xs font-bold tracking-[0.18em] uppercase text-primary/50">{label}</span>
    <select
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className="mt-2 h-12 w-full rounded-xl border border-primary/10 bg-white px-4 text-sm font-medium text-primary focus:outline-none focus:ring-2 focus:ring-accent disabled:cursor-not-allowed disabled:bg-muted/60"
    >
      {options.map((option) => <option key={option} value={option}>{option}</option>)}
    </select>
  </label>
);

const SectionIntro = ({ overline, title, text }) => (
  <FadeUp>
    <Overline>{overline}</Overline>
    <h2 className="mt-4 max-w-4xl font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-primary">{title}</h2>
    {text && <p className="mt-5 max-w-3xl text-base sm:text-lg text-muted-foreground leading-relaxed">{text}</p>}
  </FadeUp>
);

const PillGrid = ({ items, testPrefix, dark = false }) => (
  <div className="mt-10 flex flex-wrap gap-3">
    {items.map((item) => (
      <span
        key={item}
        data-testid={`${testPrefix}-${slug(item)}`}
        className={`rounded-full border px-4 py-2 text-sm font-medium ${dark ? "border-white/15 bg-white/5 text-white/80" : "border-primary/10 bg-white text-primary/80"}`}
      >
        {item}
      </span>
    ))}
  </div>
);

const INDUSTRY_PILLARS = [
  ["01", "Discover", "Find knowledge, evidence, opportunities and intelligence."],
  ["02", "Decide", "Understand choices, risks, opportunities and priorities."],
  ["03", "Build", "Create agents, workflows, applications and intelligence."],
  ["04", "Create", "Create marketing documents, articles, images, videos and logos."],
  ["05", "Operate", "Run governed, intelligent work across the organization."],
  ["06", "Transform", "Change functions, operating models and organizations."],
];

const CONNECTORS = ["APIs", "Databases", "Files", "CRM", "ERP", "EHR", "Email", "Cloud", "Identity", "Analytics", "Data Services", "Webhooks"];

const IndustryPillars = () => {
  const [industry, setIndustry] = useState("Banking & Financial Services");

  return (
    <section className="pb-24 lg:pb-32" data-testid="industry-pillars">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <FadeUp>
          <div className="rounded-3xl border border-primary/10 bg-white p-6 sm:p-8 lg:p-10 shadow-xl shadow-primary/5">
            <div className="grid gap-8 lg:grid-cols-12 lg:items-end">
              <div className="lg:col-span-7">
                <Overline>Select Industry</Overline>
                <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-primary">
                  One platform. Six ways to move any industry forward.
                </h2>
              </div>
              <div className="lg:col-span-5">
                <Select
                  label="Industry"
                  value={industry}
                  onChange={setIndustry}
                  options={INDUSTRY_DOMAINS.map(([name]) => name)}
                  testId="industry-select"
                />
              </div>
            </div>

            <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="industry-pillar-grid">
              {INDUSTRY_PILLARS.map(([number, title, text], i) => (
                <FadeUp key={title} delay={i * 0.05}>
                  <div className="group h-full rounded-2xl border border-primary/10 bg-background p-7 transition-all duration-300 hover:-translate-y-1 hover:border-accent/40 hover:shadow-lg hover:shadow-accent/10" data-testid={`pillar-${slug(title)}`}>
                    <span className="text-xs font-bold tracking-[0.2em] text-accent">{number}</span>
                    <h3 className="mt-5 font-display text-2xl font-medium tracking-tight text-primary">{title}</h3>
                    <p className="mt-3 text-sm sm:text-base text-muted-foreground leading-relaxed">{text}</p>
                    <p className="mt-5 text-xs font-bold uppercase tracking-[0.16em] text-primary/40 group-hover:text-accent transition-colors">{industry}</p>
                  </div>
                </FadeUp>
              ))}
            </div>

            <div className="mt-10 rounded-2xl border border-accent/25 bg-accent/5 p-6" data-testid="pillar-connectors">
              <p className="text-xs font-bold tracking-[0.2em] uppercase text-accent">Connectors</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {CONNECTORS.map((connector) => (
                  <span key={connector} className="rounded-full border border-primary/10 bg-white px-3 py-1.5 text-xs font-semibold text-primary/75">
                    {connector}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </FadeUp>
      </div>
    </section>
  );
};

const Architecture = () => (
  <section className="py-24 lg:py-32 bg-primary text-white" data-testid="universal-architecture">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <FadeUp>
        <Overline>Universal architecture</Overline>
        <h2 className="mt-4 max-w-4xl font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight">
          Not Industry → Agents. Intelligence → Agents, GitBranchs and Software.
        </h2>
      </FadeUp>
      <div className="mt-14 grid gap-4 lg:grid-cols-5">
        {[
          [Globe, "Industry Intelligence", "Industry → jurisdiction → regulation → function → process → use case"],
          [Buildings, "Organization Intelligence", "Knowledge, policies, data, products, users and authorized systems"],
          [Cpu, "AI Intelligence", "Models, prompts, tools, controls, risk and governance"],
          [GitBranch, "Execution", "Agents, workflows, applications, integrations and human approvals"],
          [Network, "Action / Output", "Reports, apps, decisions, tasks, automations and consulting"],
        ].map(([Icon, title, text], i) => (
          <FadeUp key={title} delay={i * 0.06}>
            <div className="relative h-full rounded-2xl border border-white/15 bg-white/5 p-6" data-testid={`architecture-${slug(title)}`}>
              <Icon size={26} weight="duotone" className="text-accent" />
              <h3 className="mt-5 font-display text-xl font-medium tracking-tight">{title}</h3>
              <p className="mt-3 text-sm text-white/60 leading-relaxed">{text}</p>
              {i < 4 && <span className="hidden lg:block absolute -right-4 top-1/2 -translate-y-1/2 text-accent">→</span>}
            </div>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const HUB_NODES = [
  ["Healthcare", 50, 10],
  ["Banking", 82, 24],
  ["Government", 90, 58],
  ["Manufacturing", 72, 86],
  ["Nonprofit", 28, 86],
  ["Energy", 10, 58],
  ["Retail", 18, 24],
  ["Education", 50, 96],
];

const HubSpoke = () => (
  <section className="py-24 lg:py-32 bg-white border-b border-primary/10" data-testid="universal-hub-spoke">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <SectionIntro
        overline="Hub-and-spoke model"
        title="One universal intelligence hub. Every industry connects as a spoke."
        text="The same core engine powers industry-specific agents, workflows, applications, connectors and governance — without rebuilding the platform for every vertical."
      />
      <FadeUp delay={0.15}>
        <div className="relative mt-14 h-[560px] overflow-hidden rounded-3xl bg-primary text-white" data-testid="hub-spoke-visual">
          <svg className="absolute inset-0 h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
            {HUB_NODES.map(([name, x, y]) => (
              <line key={name} x1="50" y1="50" x2={x} y2={y} stroke="rgba(255,255,255,0.22)" strokeWidth="0.25" strokeDasharray="1 1" />
            ))}
            <circle cx="50" cy="50" r="18" fill="none" stroke="rgba(255,255,255,0.16)" strokeWidth="0.25" />
            <circle cx="50" cy="50" r="32" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="0.25" />
          </svg>
          <div className="absolute left-1/2 top-1/2 z-10 w-56 -translate-x-1/2 -translate-y-1/2 rounded-3xl border border-accent/40 bg-white/10 p-6 text-center backdrop-blur-xl" data-testid="hub-spoke-center">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-accent">Universal Hub</p>
            <h3 className="mt-3 font-display text-2xl font-medium">Anxya AI OS & PaaS</h3>
            <p className="mt-3 text-xs text-white/60 leading-relaxed">Agents · Workflows · Apps · Data · Governance</p>
          </div>
          {HUB_NODES.map(([name, x, y], i) => (
            <div
              key={name}
              data-testid={`hub-spoke-${slug(name)}`}
              className="absolute z-10 -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-xs sm:text-sm font-semibold text-white backdrop-blur-md"
              style={{ left: `${x}%`, top: `${y}%`, transitionDelay: `${i * 40}ms` }}
            >
              {name}
            </div>
          ))}
        </div>
      </FadeUp>
    </div>
  </section>
);

const Taxonomy = () => (
  <section className="py-24 lg:py-32 bg-white border-y border-primary/10" data-testid="universal-taxonomy">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <SectionIntro
        overline="Universal Industry Master Taxonomy"
        title="Every major industry domain, designed to expand dynamically."
        text="Industry classification is hierarchical, not flat. Anxya treats geography, jurisdiction, industry, sub-industry, organization type and function as connected intelligence rather than a hard-coded list."
      />
      <div className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {INDUSTRY_DOMAINS.map(([domain, children], i) => (
          <FadeUp key={domain} delay={Math.min(i * 0.02, 0.2)}>
            <details className="group h-full rounded-2xl border border-primary/10 bg-background p-6 open:bg-white open:shadow-lg open:shadow-primary/5" data-testid={`industry-${slug(domain)}`}>
              <summary className="cursor-pointer list-none font-display text-lg font-medium tracking-tight text-primary">
                {domain}
                <span className="mt-2 block text-xs font-bold uppercase tracking-[0.18em] text-accent">{children.length} sub-industries</span>
              </summary>
              <div className="mt-5 flex flex-wrap gap-2">
                {children.map((child) => <span key={child} className="rounded-full bg-muted px-3 py-1.5 text-xs font-medium text-primary/75">{child}</span>)}
              </div>
            </details>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const FunctionLibrary = () => (
  <section className="py-24 lg:py-32" data-testid="universal-functions">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <SectionIntro
        overline="Universal Function Library"
        title="Every organization function becomes an agentic surface."
        text="Strategy, finance, HR, sales, marketing, customer, operations, supply chain, manufacturing, R&D, product, technology, data, legal, risk, compliance, security, procurement and communications."
      />
      <div className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {FUNCTION_GROUPS.map(([group, items], i) => (
          <FadeUp key={group} delay={Math.min(i * 0.025, 0.2)}>
            <details className="h-full rounded-2xl border border-primary/10 bg-white p-6 open:border-accent/40" data-testid={`function-${slug(group)}`}>
              <summary className="cursor-pointer list-none font-display text-xl font-medium tracking-tight text-primary">{group}</summary>
              <div className="mt-5 space-y-2">
                {items.map((item) => <p key={item} className="text-sm text-muted-foreground">{item}</p>)}
              </div>
            </details>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const Builders = () => (
  <section className="py-24 lg:py-32 bg-white border-y border-primary/10" data-testid="universal-builders">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <SectionIntro
        overline="Build anything"
        title="Software, web apps, agents and workflows from the same intelligence layer."
      />
      <div className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {[
          ["AI Software Builder", "Requirements, architecture, database, UI, APIs, workflows, security, compliance, tests, documentation and deployment."],
          ["WebApp Builder", "Frontend, backend, authentication, roles, permissions, notifications, integrations, analytics, audit trail and deployment package."],
          ["Agent Builder", "Create, clone, customize, configure, test, publish, share, sell and run agents with knowledge, permissions and approvals."],
          ["GitBranch Builder", "Create, clone, schedule, monitor, publish and sell workflows with human tasks, AI tasks, API calls, approvals and audit checkpoints."],
        ].map(([title, text], i) => (
          <FadeUp key={title} delay={i * 0.06}>
            <div className="h-full rounded-2xl border border-primary/10 bg-background p-7" data-testid={`builder-${slug(title)}`}>
              <Stack size={26} weight="duotone" className="text-accent" />
              <h3 className="mt-5 font-display text-xl font-medium tracking-tight text-primary">{title}</h3>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{text}</p>
            </div>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const IntelligenceEngines = () => (
  <section className="py-24 lg:py-32 bg-primary text-white" data-testid="universal-engines">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <FadeUp>
        <Overline>Assessment, consulting, risk and compliance</Overline>
        <h2 className="mt-4 max-w-4xl font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight">
          A consulting firm, risk office and compliance engine — in software.
        </h2>
      </FadeUp>
      <div className="mt-14 grid gap-6 lg:grid-cols-2">
        <FadeUp>
          <div className="rounded-3xl border border-white/15 bg-white/5 p-8">
            <h3 className="font-display text-2xl font-medium">Universal Assessments</h3>
            <PillGrid dark items={ASSESSMENTS} testPrefix="assessment" />
          </div>
        </FadeUp>
        <FadeUp delay={0.1}>
          <div className="rounded-3xl border border-white/15 bg-white/5 p-8">
            <h3 className="font-display text-2xl font-medium">Consulting Engine</h3>
            <PillGrid dark items={CONSULTING} testPrefix="consulting" />
          </div>
        </FadeUp>
      </div>
      <FadeUp delay={0.15}>
        <div className="mt-6 rounded-3xl border border-accent/30 bg-accent/10 p-8">
          <h3 className="font-display text-2xl font-medium">Universal Risk Engine</h3>
          <PillGrid dark items={RISKS} testPrefix="risk" />
          <p className="mt-6 text-sm text-white/65 leading-relaxed">
            Every risk can carry cause, impact, likelihood, severity, controls, gaps, mitigation, owner, monitoring, escalation and residual risk.
          </p>
        </div>
      </FadeUp>
    </div>
  </section>
);

const OutputBuilders = () => (
  <section className="py-24 lg:py-32" data-testid="universal-output-builders">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <SectionIntro
        overline="Document, marketing and business intelligence"
        title="Read, understand, generate and act."
      />
      <div className="mt-12 grid gap-4 lg:grid-cols-3">
        {[
          ["Document Intelligence", "Contracts, policies, regulations, SOPs, reports, spreadsheets, PDFs, manuals, invoices, proposals, RFPs and research papers — extracted, compared, summarized, validated and turned into action."],
          ["Marketing Material Builder", "Positioning, messaging, campaigns, landing pages, websites, email, LinkedIn, brochures, presentations, case studies, webinars, scripts, ads, sales pitches and RFP responses."],
          ["Business Material Builder", "Business plans, strategic plans, board decks, investor materials, business cases, financial models, proposals, SOWs, policies, SOPs, training and job descriptions."],
        ].map(([title, text], i) => (
          <FadeUp key={title} delay={i * 0.07}>
            <div className="h-full rounded-2xl border border-primary/10 bg-white p-8" data-testid={`output-${slug(title)}`}>
              <h3 className="font-display text-2xl font-medium tracking-tight text-primary">{title}</h3>
              <p className="mt-4 text-sm sm:text-base text-muted-foreground leading-relaxed">{text}</p>
            </div>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const MarketplaceSection = () => (
  <section className="py-24 lg:py-32 bg-white border-y border-primary/10" data-testid="universal-marketplace">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <div className="grid gap-12 lg:grid-cols-2">
        <FadeUp>
          <Overline>Universal Marketplace</Overline>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-primary">
            Discover → Try → Run → Clone → Customize → Publish → Sell
          </h2>
          <p className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Clone + Customize is core: change industry, country, language, regulations, data, instructions, workflows, branding, integrations, permissions, output, documents, knowledge and agents.
          </p>
          <PillGrid items={MARKETPLACE} testPrefix="marketplace" />
        </FadeUp>
        <FadeUp delay={0.12}>
          <div className="rounded-3xl bg-primary p-8 lg:p-10 text-white">
            <ShieldCheck size={30} weight="duotone" className="text-accent" />
            <h3 className="mt-6 font-display text-2xl font-medium">Organization Knowledge Vault</h3>
            <p className="mt-4 text-sm sm:text-base text-white/70 leading-relaxed">
              Policies, procedures, employees, products, services, customers, vendors, contracts, regulations, standards, strategy, brand, knowledge, data, previous work, agents and workflows — all authorized before use.
            </p>
            <div className="mt-8 grid gap-3 sm:grid-cols-2">
              {ROLES.map(([role, focus]) => (
                <div key={role} className="rounded-xl border border-white/15 bg-white/5 p-4" data-testid={`role-${slug(role)}`}>
                  <p className="text-sm font-bold text-white">{role}</p>
                  <p className="mt-1 text-xs text-white/60">{focus}</p>
                </div>
              ))}
            </div>
          </div>
        </FadeUp>
      </div>
    </div>
  </section>
);

const PublicModes = () => (
  <section className="py-24 lg:py-32" data-testid="universal-public-modes">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <SectionIntro
        overline="Government and nonprofit modes"
        title="Public-purpose organizations are first-class citizens."
      />
      <div className="mt-12 grid gap-6 lg:grid-cols-2">
        <FadeUp>
          <div className="h-full rounded-3xl border border-primary/10 bg-white p-8">
            <Buildings size={28} weight="duotone" className="text-accent" />
            <h3 className="mt-5 font-display text-2xl font-medium text-primary">Government Mode</h3>
            <PillGrid items={GOVERNMENT_MODE} testPrefix="government" />
          </div>
        </FadeUp>
        <FadeUp delay={0.1}>
          <div className="h-full rounded-3xl border border-primary/10 bg-white p-8">
            <HandHeart size={28} weight="duotone" className="text-accent" />
            <h3 className="mt-5 font-display text-2xl font-medium text-primary">Nonprofit Mode</h3>
            <PillGrid items={NONPROFIT_MODE} testPrefix="nonprofit" />
          </div>
        </FadeUp>
      </div>
    </div>
  </section>
);

export default function UniversalPlatform() {
  return (
    <>
      <PageHeader
        testId="universal-platform-page"
        overline="Anxya AI OS & PaaS"
        title={["One platform. Every industry.", "Every function. Every jurisdiction."]}
        subtitle="A Universal AI Operating System and PaaS for organizations, governments and nonprofits — discover, build, run, clone, customize and deploy agents, workflows, software, assessments and consulting solutions with governance built in. Anxya Health is the first vertical implementation."
      />
      <IndustryPillars />
      <Architecture />
      <HubSpoke />
      <Taxonomy />
      <FunctionLibrary />
      <Builders />
      <IntelligenceEngines />
      <OutputBuilders />
      <MarketplaceSection />
      <PublicModes />

      <section className="py-24 lg:py-32 bg-white border-y border-primary/10" data-testid="universal-output-engine">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid gap-12 lg:grid-cols-2 items-start">
          <FadeUp>
            <Overline>Universal Output Engine</Overline>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-primary">
              WHO · WHAT · WHY · WHERE · WHEN · INDUSTRY · JURISDICTION · FUNCTION · RISK · OUTPUT
            </h2>
          </FadeUp>
          <FadeUp delay={0.12}>
            <div className="rounded-3xl border border-primary/10 bg-background p-8">
              <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
                Every output is accurate, contextual, actionable, traceable, auditable, secure, explainable, industry-appropriate and jurisdiction-aware. High-impact decisions remain human-in-the-loop by design.
              </p>
              <div className="mt-8 grid gap-3 sm:grid-cols-2">
                {["AI recommendation", "Evidence", "Reasoning summary", "Risks", "Required approvals", "Human decision point"].map((item) => (
                  <div key={item} className="rounded-xl border border-primary/10 bg-white px-4 py-3 text-sm font-semibold text-primary">{item}</div>
                ))}
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      <section className="py-24 lg:py-32" data-testid="universal-master-prompt-gate">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <FadeUp>
            <div className="rounded-3xl bg-primary p-10 lg:p-16 text-white flex flex-col lg:flex-row items-start lg:items-center justify-between gap-10">
              <div className="max-w-2xl">
                <Overline>Founder-level blueprint</Overline>
                <h2 className="mt-4 font-display text-3xl sm:text-4xl font-medium tracking-tight">
                  The full Universal AI OS master prompt is available inside the gated pitch deck.
                </h2>
                <p className="mt-5 text-sm sm:text-base text-white/65 leading-relaxed">
                  The public page shares the platform vision and architecture. The complete operating prompt — including agent, workflow, software builder, compliance, risk, marketplace, security and human-oversight instructions — is reserved for verified corporate viewers.
                </p>
              </div>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/pitch"
                  data-testid="universal-master-prompt-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-accent px-7 py-3.5 text-sm font-semibold text-primary transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-accent/30"
                >
                  <LockKey size={16} weight="bold" /> Unlock in Pitch Deck
                </Link>
                <Link
                  to="/demo"
                  data-testid="universal-demo-btn"
                  className="inline-flex items-center gap-2 rounded-full border border-white/30 px-7 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:border-accent"
                >
                  Talk to Us <ArrowUpRight size={16} weight="bold" />
                </Link>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>
    </>
  );
}
